import React, { useState, useEffect, useCallback } from 'react';

import { CheckBox } from 'devextreme-react/check-box';
import { SelectBox } from 'devextreme-react/select-box';
import { NumberBox } from 'devextreme-react/number-box';
import { DateBox } from 'devextreme-react/date-box';

import DataSourceCharacteristicsApi from '../../api/DataSourceCharacteristicsApi';
import ScheduleTypeApi from '../../api/ScheduleTypeApi';

import DataTableReadDto from '../../classes/dtos/pathcommonmodule/DataTableReadDto';
import DataSourceDataTableReadDto from '../../classes/dtos/pathcommonmodule/DataSourceDataTableReadDto';
import DataSourceCharacteristicsDto from '../../classes/dtos/datasourcemodule/DataSourceCharacteristicsDto';
import PathSynchroSetupDto from '../../classes/dtos/pathsynchromodule/PathSynchroSetupDto';
import ScheduleDto from '../../classes/dtos/schedulermodule/ScheduleDto';
import ScheduleTypeListDto from '../../classes/dtos/schedulermodule/ScheduleTypeListDto';

import './SynchroSetup.css';

interface SynchroSetupProps {
    TargetDataTable: DataTableReadDto | null;
    PathSynchroSetup: PathSynchroSetupDto | null;
    PathSchedule: ScheduleDto | null;
    SetPathSynchroSetup(value: PathSynchroSetupDto): void;
    SetPathSchedule(value: ScheduleDto): void;
}

const SynchroSetup: React.FC<SynchroSetupProps> = ({ TargetDataTable, PathSynchroSetup, PathSchedule, SetPathSynchroSetup, SetPathSchedule }) => {

    const [dataSourceCharacteristics, setDataSourceCharacteristics] = useState<DataSourceCharacteristicsDto | null>(null);
    const [scheduleTypes, setScheduleTypes] = useState<ScheduleTypeListDto[] | null>(null);

    const dataSourceCharacteristicsLoad = useCallback(async () => {
        if (!dataSourceCharacteristics) {
            if (TargetDataTable instanceof DataSourceDataTableReadDto && TargetDataTable.DataSourceId !== null) {
                DataSourceCharacteristicsApi.GetDataSourceCharacteristicsByIdAsync(TargetDataTable.DataSourceId)
                    .then(getDataSourceCharacteristicsResult => {
                        setDataSourceCharacteristics(getDataSourceCharacteristicsResult.Result);
                    });
            } else {
                setDataSourceCharacteristics(new DataSourceCharacteristicsDto(false, false, false, false, false, false, false, false));
            }
        }
    }, [dataSourceCharacteristics, TargetDataTable]);

    useEffect(() => {
        dataSourceCharacteristicsLoad()
    }, [dataSourceCharacteristicsLoad]);

    const scheduleTypesLoad = useCallback(async () => {
        if (!scheduleTypes) {

            ScheduleTypeApi.GetScheduleTypesAsync()
                .then(getScheduleTypes => {
                    setScheduleTypes(getScheduleTypes.Result);
                });
        }

    }, [scheduleTypes]);

    useEffect(() => {
        scheduleTypesLoad()
    }, [scheduleTypesLoad]);


    const onAdditionActivatedValueChanged = useCallback((e) => {
        let dto = PathSynchroSetup;
        if (dto !== null) {
            dto.AdditionActivated = e.value ?? false;
            SetPathSynchroSetup(dto);
        }
    }, []);

    const onUpdateActivatedValueChanged = useCallback((e) => {
        let dto = PathSynchroSetup;
        if (dto !== null) {
            dto.UpdateActivated = e.value ?? false;
            SetPathSynchroSetup(dto);
        }
    }, []);

    const onDeletionActivatedValueChanged = useCallback((e) => {
        let dto = PathSynchroSetup;
        if (dto !== null) {
            dto.DeletionActivated = e.value ?? false;
            SetPathSynchroSetup(dto);
        }
    }, []);

    const onPathScheduleIsActiveValueChanged = useCallback((e) => {
        let dto = PathSchedule;
        if (dto !== null) {
            dto.IsActive = e.value ?? false;
            SetPathSchedule(dto);
        }
    }, [])

    const onPathScheduleTypeValueChanged = useCallback((e) => {
        let dto = PathSchedule;
        if (dto !== null) {
            dto.Type = e.value ?? 0;
            SetPathSchedule(dto);
        }
    }, []);

    const onPathScheduleIntervalValueChanged = useCallback((e) => {
        let dto = PathSchedule;
        if (dto !== null) {
            dto.Interval = e.value ?? 0;
            SetPathSchedule(dto);
        }
    }, []);

    const onPathScheduleTimeValueChanged = useCallback((e) => {
        let dto = PathSchedule;
        if (dto !== null) {
            dto.Time = e.value ?? 0;
            SetPathSchedule(dto);
        }
    }, []);

    let additionEnabled = dataSourceCharacteristics?.CreateAvailable ?? false;
    let updateEnabled = dataSourceCharacteristics?.UpdateAvailable ?? false;
    let deletionEnabled = dataSourceCharacteristics?.DeleteAvailable ?? false;

    return (
        <React.Fragment>
            <div className='BlockCard'>
                <div className='BlockHeader'>
                    <div className='BlockContent'>
                        <CheckBox disabled={!additionEnabled} value={PathSynchroSetup?.AdditionActivated} onValueChanged={onAdditionActivatedValueChanged} />
                        <span>
                            {additionEnabled ? ("Data addition authorized") : ("The target doesn't manage the addition")}
                        </span>
                    </div>
                </div>
                <div className='BlockHeader'>
                    <div className='BlockContent'>
                        <CheckBox disabled={!updateEnabled} value={PathSynchroSetup?.UpdateActivated} onValueChanged={onUpdateActivatedValueChanged} />
                        <span>
                            {updateEnabled ? ("Data update authorized") : ("The target doesn't manage the update")}
                        </span>
                    </div>
                </div>
                <div className='BlockHeader'>
                    <div className='BlockContent'>
                        <CheckBox disabled={!deletionEnabled} value={PathSynchroSetup?.DeletionActivated} onValueChanged={onDeletionActivatedValueChanged} />
                        <span>
                            {deletionEnabled ? ("Data deletion authorized") : ("The target doesn't manage the deletion")}
                        </span>
                    </div>
                </div>
            </div>
            <div className='BlockCard'>
                <div className='BlockHeader'>
                    <div className='BlockContent'>
                        <CheckBox value={PathSchedule?.IsActive} onValueChanged={onPathScheduleIsActiveValueChanged} />
                        <span>
                            Scheduling enabled
                        </span>
                        <SelectBox value={PathSchedule?.Type} dataSource={scheduleTypes} displayExpr="Name" valueExpr="Id" onValueChanged={onPathScheduleTypeValueChanged} />
                        <NumberBox visible={PathSchedule?.Type === 1} value={PathSchedule?.Interval} min={1} max={120} showSpinButtons={true} width={120} onValueChanged={onPathScheduleIntervalValueChanged} />
                        <DateBox visible={PathSchedule?.Type === 2} value={PathSchedule?.Time} type="time" displayFormat="HH:mm" width={120} onValueChanged={onPathScheduleTimeValueChanged} />
                        {PathSchedule?.Type === 1 ? (<span >Minutes</span>) : ""}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default SynchroSetup;